import React, { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PRESET_LEARNERS = ['L-1001', 'L-2002', 'L-3003', 'ashley@gmail.com'];

export default function TestReceiptUpload({ onSwitchView }) {
  const [learnerId, setLearnerId] = useState('L-1001');
  const [activeLearner, setActiveLearner] = useState('L-1001');
  const [receipts, setReceipts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // File selection state
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Notifications
  const [notification, setNotification] = useState(null); // { type: 'success'|'error'|'info', message: '' }

  // S3 Health status
  const [s3Status, setS3Status] = useState(null);

  // Modal preview
  const [previewModal, setPreviewModal] = useState(null); // { url, filename, type }

  const fileInputRef = useRef(null);

  // Check backend & S3 connectivity on mount
  useEffect(() => {
    fetchS3Health();
    fetchReceipts('L-1001');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchS3Health = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/receipts/health/status`);
      const data = await res.json();
      setS3Status(data);
    } catch (err) {
      console.warn('Could not check S3 health status:', err);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification(prev => (prev?.message === message ? null : prev));
    }, 6000);
  };

  const fetchReceipts = async (idToFetch) => {
    const targetId = (idToFetch || learnerId || '').trim();
    if (!targetId) {
      showNotification('error', 'Please enter a valid Learner ID.');
      return;
    }

    setLoadingList(true);
    setActiveLearner(targetId);

    try {
      const res = await fetch(`${API_BASE}/api/receipts/${encodeURIComponent(targetId)}`);
      const data = await res.json();

      if (res.ok && data.status === 'success') {
        setReceipts(data.data || []);
      } else {
        showNotification('error', data.message || 'Failed to retrieve receipts.');
        setReceipts([]);
      }
    } catch (err) {
      showNotification('error', `Failed to connect to backend server (${err.message}).`);
      setReceipts([]);
    } finally {
      setLoadingList(false);
    }
  };

  const validateAndSetFile = (file) => {
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      showNotification('error', 'Unsupported file type! Only PDF, PNG, and JPG/JPEG files are accepted.');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      showNotification('error', `File is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Max limit is 5MB.`);
      return;
    }

    setSelectedFile(file);

    // Create preview if it's an image
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    validateAndSetFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    const targetLearnerId = learnerId.trim();
    if (!targetLearnerId) {
      showNotification('error', 'Please enter a Learner ID before uploading.');
      return;
    }

    if (!selectedFile) {
      showNotification('error', 'Please select a PDF or Image receipt to upload.');
      return;
    }

    setUploading(true);
    setUploadProgress(20);

    const formData = new FormData();
    formData.append('learnerId', targetLearnerId);
    formData.append('file', selectedFile);

    try {
      setUploadProgress(60);
      const res = await fetch(`${API_BASE}/api/receipts/upload`, {
        method: 'POST',
        body: formData
      });

      setUploadProgress(90);
      const data = await res.json();

      if (res.ok && data.status === 'success') {
        setUploadProgress(100);
        showNotification('success', `Receipt "${data.data.filename}" successfully uploaded to S3!`);
        // Reset file selection
        setSelectedFile(null);
        setFilePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';

        // Reload receipts for current learner
        await fetchReceipts(targetLearnerId);
      } else {
        showNotification('error', data.message || 'Upload failed.');
      }
    } catch (err) {
      showNotification('error', `Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (isoString) => {
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch (e) {
      return isoString;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => showNotification('info', 'Pre-signed URL copied to clipboard! (Valid for 15 mins)'),
      () => showNotification('error', 'Failed to copy URL to clipboard.')
    );
  };

  return (
    <div style={styles.container}>
      {/* Header Banner */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerBadge}>AWS S3 Storage Integration</div>
          <h1 style={styles.title}>Payment Receipt Upload & Pre-signed URL Manager</h1>
          <p style={styles.subtitle}>
            Securely upload payment slips, bank transfer proofs, and invoices to AWS S3 with learner-isolated prefixing and 15-minute temporary pre-signed access URLs.
          </p>

          {/* S3 Connection Indicator */}
          <div style={styles.statusChips}>
            <div style={styles.statusChip}>
              <span style={styles.statusDotGreen}></span>
              <span><strong>Bucket:</strong> {s3Status?.bucket || 'bbccoe-lms'}</span>
            </div>
            <div style={styles.statusChip}>
              <span style={styles.statusDotBlue}></span>
              <span><strong>Region:</strong> {s3Status?.region || 'ap-southeast-5'}</span>
            </div>
            <div style={styles.statusChip}>
              <span style={styles.statusDotPurple}></span>
              <span><strong>Presigned Expiry:</strong> 15 Minutes</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div
          style={{
            ...styles.alert,
            ...(notification.type === 'success'
              ? styles.alertSuccess
              : notification.type === 'error'
                ? styles.alertError
                : styles.alertInfo)
          }}
        >
          <span style={styles.alertIcon}>
            {notification.type === 'success' ? '✓' : notification.type === 'error' ? '✕' : 'ℹ'}
          </span>
          <span style={{ flex: 1 }}>{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            style={styles.alertCloseBtn}
          >
            ×
          </button>
        </div>
      )}

      <div style={styles.mainGrid}>
        {/* Left Column: Learner Selector & Upload Box */}
        <div style={styles.column}>
          {/* Section 1: Learner Identity */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardIcon}>👤</div>
              <h2 style={styles.cardTitle}>Learner Identity & Prefix Isolation</h2>
            </div>
            <p style={styles.cardDesc}>
              Receipts are stored under <code style={styles.inlineCode}>receipts/{'{learnerId}'}/</code> ensuring complete multi-tenant file isolation.
            </p>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Learner ID / Key</label>
              <div style={styles.inputRow}>
                <input
                  type="text"
                  value={learnerId}
                  onChange={(e) => setLearnerId(e.target.value)}
                  placeholder="e.g. L-1001"
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={() => fetchReceipts(learnerId)}
                  disabled={loadingList}
                  style={styles.btnSecondary}
                >
                  {loadingList ? 'Loading...' : 'Load Receipts'}
                </button>
              </div>
            </div>

            <div style={styles.presetsWrapper}>
              <span style={styles.presetsLabel}>Quick Presets:</span>
              <div style={styles.presetButtons}>
                {PRESET_LEARNERS.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setLearnerId(id);
                      fetchReceipts(id);
                    }}
                    style={{
                      ...styles.presetChip,
                      ...(learnerId === id ? styles.presetChipActive : {})
                    }}
                  >
                    {id}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Section 2: Upload Receipt */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardIcon}>☁️</div>
              <h2 style={styles.cardTitle}>Upload Payment Receipt</h2>
            </div>
            <p style={styles.cardDesc}>
              Upload payment slip or bank receipt (PDF, PNG, JPG/JPEG &bull; Max 5MB).
            </p>

            <form onSubmit={handleUpload}>
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                style={{
                  ...styles.dropZone,
                  ...(isDragOver ? styles.dropZoneActive : {}),
                  ...(selectedFile ? styles.dropZoneHasFile : {})
                }}
                data-hj-suppress
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,image/png,image/jpeg,image/jpg"
                  style={{ display: 'none' }}
                />

                {selectedFile ? (
                  <div style={styles.selectedFileInfo}>
                    {filePreview ? (
                      <img
                        src={filePreview}
                        alt="Preview"
                        style={styles.previewImageThumb}
                      />
                    ) : (
                      <div style={styles.pdfIconBadge}>PDF</div>
                    )}
                    <div style={styles.selectedFileMeta}>
                      <div style={styles.selectedFileName}>{selectedFile.name}</div>
                      <div style={styles.selectedFileSize}>
                        {formatFileSize(selectedFile.size)} &bull; {selectedFile.type || 'Document'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setFilePreview(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      style={styles.removeFileBtn}
                      title="Remove selected file"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={styles.dropZonePrompt}>
                    <div style={styles.uploadCloudIcon}>📤</div>
                    <div style={styles.dropZoneText}>
                      <strong>Click to browse</strong> or drag & drop receipt file here
                    </div>
                    <div style={styles.dropZoneHint}>Supported formats: PDF, PNG, JPG, JPEG (Max 5MB)</div>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {uploading && (
                <div style={styles.progressContainer}>
                  <div style={{ ...styles.progressBar, width: `${uploadProgress}%` }}></div>
                </div>
              )}

              <button
                type="submit"
                disabled={uploading || !selectedFile || !learnerId.trim()}
                style={{
                  ...styles.btnPrimary,
                  ...(uploading || !selectedFile || !learnerId.trim() ? styles.btnDisabled : {})
                }}
              >
                {uploading ? (
                  <span>Uploading to S3...</span>
                ) : (
                  <span>Upload Receipt for {learnerId || 'Learner'}</span>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Right Column: Receipt List View */}
        <div style={styles.column}>
          <div style={{ ...styles.card, minHeight: '520px' }}>
            <div style={styles.listHeaderRow}>
              <div>
                <div style={styles.cardHeader}>
                  <div style={styles.cardIcon}>📑</div>
                  <h2 style={styles.cardTitle}>Learner Receipts Repository</h2>
                </div>
                <div style={styles.activeLearnerSubtitle}>
                  Current View: <strong style={{ color: '#4338ca' }}>{activeLearner}</strong> &bull; Prefix:{' '}
                  <code style={styles.inlineCode}>receipts/{activeLearner}/</code>
                </div>
              </div>
              <button
                type="button"
                onClick={() => fetchReceipts(activeLearner)}
                disabled={loadingList}
                style={styles.refreshBtn}
                title="Refresh pre-signed URLs"
              >
                ↻ Refresh URLs
              </button>
            </div>

            {loadingList ? (
              <div style={styles.emptyState}>
                <div style={styles.spinner}></div>
                <p style={{ marginTop: '16px', color: '#6b7280' }}>Fetching receipts from AWS S3...</p>
              </div>
            ) : receipts.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📂</div>
                <h3 style={{ margin: '0 0 8px 0', color: '#1f2937' }}>No Receipts Found</h3>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', maxWidth: '320px' }}>
                  No payment receipts have been uploaded yet under <code style={styles.inlineCode}>receipts/{activeLearner}/</code>. Use the upload panel on the left to add one.
                </p>
              </div>
            ) : (
              <div style={styles.receiptList} data-hj-suppress>
                <div style={styles.receiptsCountBadge}>
                  Found {receipts.length} receipt{receipts.length === 1 ? '' : 's'} with active 15-minute pre-signed URLs
                </div>

                {receipts.map((item, idx) => {
                  const isPdf = item.filename.toLowerCase().endsWith('.pdf');
                  return (
                    <div key={item.key || idx} style={styles.receiptCard}>
                      <div style={styles.receiptCardMain}>
                        <div style={isPdf ? styles.pdfIcon : styles.imgIcon}>
                          {isPdf ? 'PDF' : 'IMG'}
                        </div>
                        <div style={styles.receiptDetails}>
                          <div style={styles.receiptTitle} title={item.filename}>
                            {item.filename}
                          </div>
                          <div style={styles.receiptMeta}>
                            <span>📦 {formatFileSize(item.size)}</span>
                            <span>&bull;</span>
                            <span>🕒 {formatDate(item.lastModified)}</span>
                          </div>
                          <div style={styles.receiptKey} title={item.key}>
                            Key: {item.key}
                          </div>
                        </div>
                      </div>

                      <div style={styles.receiptActions}>
                        <a
                          href={item.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.actionBtnPrimary}
                        >
                          👁️ View / Download
                        </a>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(item.downloadUrl)}
                          style={styles.actionBtnSecondary}
                          title="Copy temporary pre-signed link"
                        >
                          📋 Copy URL
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Direct In-App Preview Modal */}
      {previewModal && (
        <div style={styles.modalOverlay} onClick={() => setPreviewModal(null)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>{previewModal.filename}</h3>
              <button
                type="button"
                onClick={() => setPreviewModal(null)}
                style={styles.modalCloseBtn}
              >
                ✕
              </button>
            </div>
            <div style={styles.modalBody} data-hj-suppress>
              {previewModal.type === 'pdf' ? (
                <iframe
                  src={previewModal.url}
                  title="PDF Receipt Preview"
                  style={{ width: '100%', height: '500px', border: 'none', borderRadius: '8px' }}
                />
              ) : (
                <img
                  src={previewModal.url}
                  alt="Receipt Preview"
                  style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px 16px 60px 16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    color: '#1f2937',
    backgroundColor: '#f8fafc',
    minHeight: '100vh'
  },
  header: {
    background: 'linear-gradient(135deg, #312e81 0%, #4338ca 50%, #6366f1 100%)',
    borderRadius: '16px',
    padding: '36px 32px',
    color: '#ffffff',
    marginBottom: '28px',
    boxShadow: '0 10px 25px -5px rgba(67, 56, 202, 0.3)'
  },
  headerContent: {
    maxWidth: '850px'
  },
  headerBadge: {
    display: 'inline-block',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(8px)',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: '12px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    margin: '0 0 10px 0',
    lineHeight: '1.2'
  },
  subtitle: {
    fontSize: '15px',
    lineHeight: '1.5',
    color: '#e0e7ff',
    margin: '0 0 20px 0'
  },
  statusChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px'
  },
  statusChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#f8fafc'
  },
  statusDotGreen: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#10b981'
  },
  statusDotBlue: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#38bdf8'
  },
  statusDotPurple: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#c084fc'
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: '24px',
    alignItems: 'start'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px'
  },
  cardIcon: {
    fontSize: '22px'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#0f172a',
    margin: 0
  },
  cardDesc: {
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 20px 0',
    lineHeight: '1.4'
  },
  inlineCode: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '12px'
  },
  inputGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#334155',
    marginBottom: '6px'
  },
  inputRow: {
    display: 'flex',
    gap: '8px'
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    backgroundColor: '#f8fafc'
  },
  btnSecondary: {
    padding: '10px 16px',
    backgroundColor: '#e2e8f0',
    color: '#334155',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  presetsWrapper: {
    marginTop: '12px'
  },
  presetsLabel: {
    fontSize: '12px',
    color: '#64748b',
    display: 'block',
    marginBottom: '6px'
  },
  presetButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  presetChip: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s'
  },
  presetChipActive: {
    backgroundColor: '#e0e7ff',
    color: '#4338ca',
    borderColor: '#c7d2fe',
    fontWeight: '600'
  },
  dropZone: {
    border: '2px dashed #cbd5e1',
    borderRadius: '12px',
    padding: '24px 16px',
    textAlign: 'center',
    backgroundColor: '#f8fafc',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '16px'
  },
  dropZoneActive: {
    borderColor: '#6366f1',
    backgroundColor: '#eef2ff'
  },
  dropZoneHasFile: {
    borderColor: '#10b981',
    backgroundColor: '#f0fdf4'
  },
  dropZonePrompt: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px'
  },
  uploadCloudIcon: {
    fontSize: '32px',
    marginBottom: '4px'
  },
  dropZoneText: {
    fontSize: '14px',
    color: '#334155'
  },
  dropZoneHint: {
    fontSize: '12px',
    color: '#94a3b8'
  },
  selectedFileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px'
  },
  previewImageThumb: {
    width: '48px',
    height: '48px',
    objectFit: 'cover',
    borderRadius: '6px',
    border: '1px solid #e2e8f0'
  },
  pdfIconBadge: {
    width: '48px',
    height: '48px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    fontWeight: '700',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px'
  },
  selectedFileMeta: {
    flex: 1,
    textAlign: 'left'
  },
  selectedFileName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#0f172a',
    wordBreak: 'break-all'
  },
  selectedFileSize: {
    fontSize: '12px',
    color: '#64748b'
  },
  removeFileBtn: {
    backgroundColor: '#fee2e2',
    color: '#ef4444',
    border: 'none',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  progressContainer: {
    height: '6px',
    backgroundColor: '#e2e8f0',
    borderRadius: '9999px',
    overflow: 'hidden',
    marginBottom: '16px'
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#4f46e5',
    transition: 'width 0.3s ease'
  },
  btnPrimary: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.25)',
    transition: 'all 0.2s'
  },
  btnDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
    boxShadow: 'none'
  },
  listHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '10px'
  },
  activeLearnerSubtitle: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '2px'
  },
  refreshBtn: {
    padding: '6px 12px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  receiptsCountBadge: {
    backgroundColor: '#eef2ff',
    color: '#4338ca',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '14px'
  },
  receiptList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  receiptCard: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    transition: 'box-shadow 0.2s'
  },
  receiptCardMain: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  pdfIcon: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    fontWeight: '700',
    fontSize: '11px',
    padding: '8px 10px',
    borderRadius: '6px',
    letterSpacing: '0.05em'
  },
  imgIcon: {
    backgroundColor: '#dbeafe',
    color: '#2563eb',
    fontWeight: '700',
    fontSize: '11px',
    padding: '8px 10px',
    borderRadius: '6px',
    letterSpacing: '0.05em'
  },
  receiptDetails: {
    flex: 1,
    minWidth: 0
  },
  receiptTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#0f172a',
    wordBreak: 'break-all',
    marginBottom: '4px'
  },
  receiptMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '4px'
  },
  receiptKey: {
    fontSize: '11px',
    color: '#94a3b8',
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  receiptActions: {
    display: 'flex',
    gap: '8px',
    borderTop: '1px solid #f1f5f9',
    paddingTop: '10px'
  },
  actionBtnPrimary: {
    flex: 1,
    padding: '8px 14px',
    backgroundColor: '#4338ca',
    color: '#ffffff',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block'
  },
  actionBtnSecondary: {
    padding: '8px 12px',
    backgroundColor: '#ffffff',
    color: '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center'
  },
  spinner: {
    width: '36px',
    height: '36px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#4f46e5',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  alert: {
    padding: '12px 16px',
    borderRadius: '10px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '14px',
    fontWeight: '500',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  alertSuccess: {
    backgroundColor: '#ecfdf5',
    color: '#065f46',
    border: '1px solid #a7f3d0'
  },
  alertError: {
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    border: '1px solid #fecaca'
  },
  alertInfo: {
    backgroundColor: '#eff6ff',
    color: '#1e40af',
    border: '1px solid #bfdbfe'
  },
  alertIcon: {
    fontWeight: '700',
    fontSize: '16px'
  },
  alertCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: 'inherit',
    padding: '0 4px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modalCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '720px',
    padding: '20px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    borderBottom: '1px solid #e2e8f0',
    paddingBottom: '12px'
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#64748b',
    cursor: 'pointer'
  },
  modalBody: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
};
